import { Palette } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { pantoneBookLabel } from './pantone';
import { AddTile, EmptyState, MicroLabel, SectionHead, cn } from './ui/primitives';
import type { BrandSwatch } from './types';

/**
 * The palette, primaries first and larger.
 *
 * ── Why two rows and not one grid ──
 *
 * The old page flattened all four categories into a single five-up grid, where
 * a primary and a neutral were the same size and in no particular order. That
 * throws away the only thing the categories encode: which colors ARE the brand
 * and which support them. Primaries get a wide row of their own; the rest share
 * a tighter grid underneath, labelled by category.
 *
 * ── Why the swatch copies ──
 *
 * The single most common thing anyone does on this page is take a hex into a
 * design tool. That was a select-and-copy against a 12px mono string. Now the
 * swatch itself is the copy control, and it says so on hover.
 *
 * The confirmation is a pill on the swatch rather than a toast: copying five
 * hexes in a row is normal here, and five stacked toasts is worse than five
 * cards that each tick and settle.
 */
export function BrandColors({
	swatches,
	onAdd,
	renderActions,
	action,
}: {
	swatches: BrandSwatch[];
	onAdd?: () => void;
	/** The per-card hover controls — edit, remove. The app's, since it owns the
	 * modal and the mutation. */
	renderActions?: (swatch: BrandSwatch) => ReactNode;
	action?: ReactNode;
}) {
	const primary = swatches.filter((s) => s.category === 'primary');
	const rest = swatches.filter((s) => s.category !== 'primary');
	/* "· pantone" only when the palette actually carries print specs — on a
	   screen-only brand it would advertise something that isn't there. */
	const anyPantone = swatches.some((s) => s.pantone);

	if (swatches.length === 0) {
		return (
			<>
				<SectionHead title="Colors" action={action} />
				<EmptyState
					icon={Palette}
					title="No colors defined"
					hint="Add the brand's colors to build a palette designers can copy from."
				/>
			</>
		);
	}

	return (
		<>
			<SectionHead
				title="Colors"
				meta={`${swatches.length} ${swatches.length === 1 ? 'color' : 'colors'} · hex${
					anyPantone ? ' · pantone' : ''
				}`}
				action={action}
			/>

			{primary.length > 0 && (
				<div className="mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(11.25rem,1fr))] gap-3.5">
					{primary.map((swatch) => (
						<SwatchCard
							key={`${swatch.category}-${swatch.index}`}
							swatch={swatch}
							large
							actions={renderActions?.(swatch)}
						/>
					))}
				</div>
			)}

			<div className="grid grid-cols-[repeat(auto-fill,minmax(11.25rem,1fr))] gap-3.5">
				{rest.map((swatch) => (
					<SwatchCard
						key={`${swatch.category}-${swatch.index}`}
						swatch={swatch}
						actions={renderActions?.(swatch)}
					/>
				))}
				{onAdd && (
					<AddTile label="Add color" onClick={onAdd} className="min-h-[8.875rem]" />
				)}
			</div>
		</>
	);
}

function SwatchCard({
	swatch,
	large,
	actions,
}: {
	swatch: BrandSwatch;
	large?: boolean;
	actions?: ReactNode;
}) {
	const [copied, setCopied] = useState(false);

	const copy = () => {
		navigator.clipboard
			.writeText(swatch.hex)
			.then(() => {
				setCopied(true);
				window.setTimeout(() => setCopied(false), 1400);
			})
			.catch(() => {});
	};

	return (
		<div className="group flex flex-col overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface transition-colors duration-[var(--dur)] hover:border-line-2">
			<div className="relative">
				<button
					type="button"
					onClick={copy}
					title={`Copy ${swatch.hex}`}
					aria-label={`Copy ${swatch.hex}`}
					className={cn('block w-full', large ? 'h-[6.875rem]' : 'h-[4.5rem]')}
					style={{ backgroundColor: swatch.hex }}
				/>
				{copied && (
					<span className="pointer-events-none absolute left-2 top-2 rounded-[var(--r-1)] bg-black/55 px-1.5 py-0.5 text-2xs font-medium text-white">
						Copied
					</span>
				)}
				{actions && (
					<span className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-[var(--dur-fast)] group-hover:opacity-100 focus-within:opacity-100">
						{actions}
					</span>
				)}
			</div>

			<div className="px-3.5 py-3">
				<div className="mb-1 flex items-center justify-between gap-2">
					<span className="truncate text-sm font-medium text-text-1">
						{swatch.name || swatch.hex.toUpperCase()}
					</span>
					{swatch.category !== 'primary' && (
						<MicroLabel className="shrink-0">{swatch.category}</MicroLabel>
					)}
				</div>
				<div className="flex items-baseline justify-between gap-2 font-mono text-xs text-text-3">
					<span>{swatch.hex}</span>
					{swatch.pantone && (
						<span
							className="truncate text-text-4"
							title={`PANTONE ${swatch.pantone.code} · ${pantoneBookLabel(swatch.pantone)}`}
						>
							PMS {swatch.pantone.code}
						</span>
					)}
				</div>
				{swatch.usage && (
					<div className="mt-1.5 text-xs leading-relaxed text-text-4">{swatch.usage}</div>
				)}
			</div>
		</div>
	);
}
